/**
 * PostHog client-side helpers.
 *
 * These are thin wrappers around posthog-js that guard against the SDK not
 * being loaded (e.g. missing token, SSR context) and keep the import dynamic
 * so posthog-js is never pulled into server bundles.
 */

import posthog from 'posthog-js';

/**
 * Events captured before `posthog.init()` completes. posthog-js DROPS
 * pre-init `capture()` calls (it only logs "You must initialize PostHog
 * before calling posthog.capture"), and init is deferred to an idle window
 * of up to 3 s (see `src/instrumentation-client.ts`) — so events fired on a
 * fresh document load (e.g. `purchase` on the order confirmation page after
 * a payment redirect) raced init and were silently lost (MWP-899). Buffer
 * them here and replay via `flushPendingPosthogEvents()` once init runs.
 *
 * If PostHog never initialises (missing token, opted out), the buffer is
 * simply never flushed — same net result as the old drop, without the race.
 */
const pendingEvents: Array<{
  eventName: string;
  properties?: Record<string, unknown>;
}> = [];

const MAX_PENDING_EVENTS = 20;

/**
 * Capture a custom event in PostHog.
 *
 * Safe to call on the server (no-op) or before PostHog loads — pre-init
 * events are buffered and replayed after init.
 * PostHog's own opt-out mechanism handles consent — no extra check needed.
 *
 * @param eventName  Event name (e.g. "add_to_cart", "purchase")
 * @param properties Optional flat property bag
 */
export function posthogCapture(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) {
    if (
      typeof window !== 'undefined' &&
      pendingEvents.length < MAX_PENDING_EVENTS
    ) {
      pendingEvents.push({ eventName, properties });
    }
    return;
  }
  posthog.capture(eventName, properties);
}

/**
 * Replay events buffered before init. Called from `posthog.init()`'s
 * `loaded` callback in `src/instrumentation-client.ts`.
 */
export function flushPendingPosthogEvents(): void {
  if (!posthog.__loaded) {
    return;
  }
  while (pendingEvents.length > 0) {
    const event = pendingEvents.shift();
    if (event) {
      posthog.capture(event.eventName, event.properties);
    }
  }
}

/**
 * Identify the current user in PostHog after login or registration.
 *
 * @param customerId  Medusa customer ID (used as the PostHog distinct ID)
 * @param traits      Optional person properties (email, name, etc.)
 */
export function posthogIdentify(
  customerId: string,
  traits?: Record<string, string>,
): void {
  if (!posthog.__loaded) return;
  posthog.identify(customerId, traits);
}

/**
 * Reset PostHog identity on sign-out so the next session starts anonymous.
 */
export function posthogReset(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
}
